'use client'

import { type FC } from 'react'

import { useRecommendationIntersection } from '../../../cross-sell'
import { usePdpAvailableExperimentActivation } from '../../../experiments/experiment-pdp-available/usePdpAvailableExperimentActivation'
import {
  gtmDataLayerPushRecommendationTracking,
  RecommendationHorizontalSlider,
  RecommendationWidgetContainer,
  RecommendationWidgetProvider,
  RecommendationWidgetTitle,
  useGetRecommendationWidgetId,
  useSwrFetchRecommendationService,
} from '../../../recommendation'
import { useProductPageContext } from '../productPageContext'
import { useShouldRenderProductRecommendationWidgetFirstImpressionSlider } from '../ProductRecommendationAreas/ProductRecommendationAreaFirstImpressionSlider'

import { useIsProductAvailableRecommendationWidgetAvailable } from './useIsProductAvailableRecommendationAreaAvailable'
import { useProductAvailableRecommendationAreaActivation } from './useProductAvailableRecommendationAreaActivation'
import { useProductAvailableRecommendationWidgetSliderRequestParams } from './useProductAvailableRecommendationAreaSliderRequestParams'

export const ProductAvailableRecommendationSliderArea: FC = () => {
  const { currentVariant } = useProductPageContext()
  const available = useIsProductAvailableRecommendationWidgetAvailable()
  const requestParams =
    useProductAvailableRecommendationWidgetSliderRequestParams()

  const shouldRenderFirstImpressionArea =
    useShouldRenderProductRecommendationWidgetFirstImpressionSlider()

  const { handleIntersection, intersected } = useRecommendationIntersection()

  const { data, error } = useSwrFetchRecommendationService(
    intersected ? requestParams : undefined,
  )
  useProductAvailableRecommendationAreaActivation({
    areaId: requestParams.widgetId,
    intersected,
  })
  usePdpAvailableExperimentActivation({
    areaId: requestParams.widgetId,
    intersected,
  })

  const widgetId = useGetRecommendationWidgetId(
    requestParams.baseServiceId,
    requestParams.widgetId,
  )

  if (
    error ||
    /*
     * Similar products will be displayed on top when there are no variants available in stock
     * https://jira.shop-apotheke.com/browse/WSHOME-734
     */
    !available ||
    // https://jira.shop-apotheke.com/browse/CRO-576
    shouldRenderFirstImpressionArea ||
    !widgetId ||
    data?.recommendations[widgetId].content.length === 0
  ) {
    return null
  }

  const widgetData = data?.recommendations[widgetId]

  return (
    <RecommendationWidgetProvider
      serviceId={requestParams.serviceId}
      widgetData={widgetData}
      widgetId={widgetId}
    >
      <RecommendationWidgetContainer
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
      >
        <RecommendationWidgetTitle className="mb-3.5 text-xl font-medium tablet:text-2xl" />
        <RecommendationHorizontalSlider
          onGtmDataLayerPush={(widgetProductId, trackingToken) => {
            gtmDataLayerPushRecommendationTracking({
              productId: currentVariant.upid,
              trackingToken,
              widgetId,
              widgetProductId,
            })
          }}
          products={widgetData?.content}
          productsPerPage={3}
        />
      </RecommendationWidgetContainer>
    </RecommendationWidgetProvider>
  )
}
